import {
  Button, Checkbox, Flex, Form, Input, message, Modal, Steps,
} from 'antd';
import { CloseOutlined, PhoneOutlined } from '@ant-design/icons';
import React, { useEffect, useState } from 'react';
import { useForm } from 'antd/es/form/Form';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { IconCheck } from '../Icon';
import usePhoneCode from './usePhoneCode';
import useSendCode from './useSendCode';
import useBusinessList from './useBusiness';
import useClaimBusinesses from './useClaimBusiness';

import styles from './index.module.scss';

interface IFormFields {
  code: string;
  phone: string;
  businesses: {
    [businessId: string]: boolean;
  };
}

interface IProps {
  isOpen: boolean;
  onCancel: () => void;
}

function ModalClaimBusinessTwo({
  isOpen,
  onCancel,
}: IProps) {
  const navigate = useNavigate();
  const [form] = useForm<IFormFields>();
  const [currentStep, setCurrentStep] = useState(0);

  const phoneCode = usePhoneCode();
  const sendCode = useSendCode();
  const businessList = useBusinessList();

  const claimBusinesses = useClaimBusinesses();

  const handleSubmitIds = async (ids: string[]): Promise<void> => {
    if (!sendCode.response?.data?.secretKey) return;

    await claimBusinesses.claimBusiness({
      secretKey: sendCode.response?.data?.secretKey || '',
      ids,
    }).then((res) => {
      if (res?.success) {
        setCurrentStep(3);
      }
    });
  };

  const steps = [
    {
      content: (
        <Form<IFormFields>
          form={form}
          autoComplete="off"
          onFinish={async () => await phoneCode.fetch({ phone: form.getFieldValue('phone') })}
          layout="vertical"
        >
          <div className={styles.description}>
            Please provide information associated with your business
          </div>
          <Form.Item
            name="phone"
            label="Phone number"
            rules={[
              { required: true, message: 'Phone number is required' },
              {
                pattern: /^\+?\d{10,15}$/,
                message: 'Enter a valid phone number',
              },
            ]}
          >
            <Input
              size="large"
              prefix={<PhoneOutlined />}
              placeholder="+1 506 123 4567"
              maxLength={16}
            />
          </Form.Item>

          <Flex justify="flex-end">
            <Button
              type="primary"
              className={styles.submit}
              onClick={form.submit}
              loading={phoneCode.loading}
              size="large"
            >
              Next
            </Button>
          </Flex>
        </Form>
      ),
    },
    {
      content: (
        <Form<IFormFields>
          form={form}
          autoComplete="off"
          layout="vertical"
          onFinish={() => sendCode.fetch({
            phone: form.getFieldValue('phone'),
            code: form.getFieldValue('code'),
          })}
        >
          <Form.Item
            name="code"
            rules={[{ required: true, message: 'Code is required' }]}
            label="Code"
          >
            <Input
              size="large"
              placeholder="888888"
              suffix={(
                <Button
                  type="text"
                  size="small"
                  disabled={phoneCode.loading}
                  htmlType="button"
                  onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    await phoneCode.fetch({ phone: form.getFieldValue('phone') });
                  }}
                  className={styles.btnResendLink}
                >
                  Resend code
                </Button>
              )}
            />
          </Form.Item>

          <Flex justify="flex-end">
            <Button
              type="primary"
              className={styles.submit}
              onClick={form.submit}
              loading={sendCode.loading}
              size="large"
            >
              Next
            </Button>
          </Flex>
        </Form>
      ),
    },
    {
      content: (
        <>
          <div className={styles.description}>
            <div>We have found the following businesses associated with your account.</div>
            <div>Please, select at least one in order to continue</div>
          </div>
          <Form<IFormFields>
            form={form}
            autoComplete="off"
            onFinish={async (values) => {
              const selectedBusinessIds = Object.keys(values.businesses || {})
                .filter((id) => values.businesses[id]);

              await handleSubmitIds(selectedBusinessIds);
            }}
            layout="vertical"
          >
            <div className={styles.description}>
              Please provide information associated with your business
            </div>
            {businessList.response?.data.map((business) => (
              <Form.Item
                key={business.id}
                name={['businesses', business.id]}
                valuePropName="checked"
                className={styles.checkBoxItem}
              >
                <Checkbox>
                  <div className={styles.businessItem}>
                    <div className={styles.bold}>{business.name}</div>
                    <div>{`${business.address || ''}, ${business.zipCode || ''}`}</div>
                  </div>
                </Checkbox>
              </Form.Item>
            ))}
            <Flex justify="flex-end">
              <Button
                type="primary"
                className={styles.submit}
                onClick={form.submit}
                size="large"
              >
                Next
              </Button>
            </Flex>
          </Form>
        </>
      ),
    },
    {
      content: (
        <>
          <div className={clsx(styles.description, styles.thinText)}>
            <div className={styles.iconWrap}>
              <div className={styles.checkIcon}>
                <IconCheck />
              </div>
            </div>
            <div className={styles.contentTitle}>Successfully!</div>
            <div>Your business claim has been successfully submitted!</div>
            <div>You can now comment and engage with users as a business owner.</div>
            <div>Thank you for taking this step to manage your business presence!</div>
          </div>
          <Flex justify="center">
            <Button
              type="primary"
              className={styles.submit}
              onClick={() => navigate('/profile')}
              size="large"
            >
              Go To Profile
            </Button>
          </Flex>
        </>
      ),
    },
  ];

  useEffect(() => {
    if (phoneCode.response) {
      setCurrentStep(1);
    }
  }, [phoneCode.response]);

  useEffect(() => {
    if (sendCode.response) {
      setCurrentStep(2);
      businessList.getBusinessList({ phone: form.getFieldValue('phone'), page: 1, pageSize: 100 });
    }
  }, [sendCode.response]);

  return (
    <Modal
      open={isOpen}
      onCancel={onCancel}
      footer={null}
      width={720}
      wrapClassName={styles.wrapper}
      className={styles.modal}
      closable
      closeIcon={<CloseOutlined className={styles.cancelBtn} />}
    >
      <div className={styles.modalContent}>
        <div className={styles.header}>
          {currentStep !== 3 ? (
            <>
              <div>
                <h3 className={styles.title}>Claim Business</h3>
                <div className={styles.subTitle}>
                  Please provide information associated with your business.
                </div>
              </div>
              <Steps current={currentStep} className={styles.steps}>
                <Steps.Step icon={currentStep > 0 ? <IconCheck /> : 1} />
                <Steps.Step icon={currentStep > 1 ? <IconCheck /> : 2} />
                <Steps.Step icon={currentStep > 2 ? <IconCheck /> : 3} />
              </Steps>
            </>
          ) : null}
        </div>
        <div className={styles.content}>{steps[currentStep].content}</div>
      </div>
    </Modal>
  );
}

export default ModalClaimBusinessTwo;
